import GlassCard from '@/components/GlassCard';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { Heading } from '@/components/Typography';
import useRoutes from '@/hooks/useRoutes';
import { Course } from '@/types';
import { BookOpen, PlayCircle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CourseHeaderProps {
    course: Course;
    lastClassViewedId?: number;
    firstClassId?: number;
}

const CourseHeader: React.FC<CourseHeaderProps> = ({
    course,
    lastClassViewedId,
    firstClassId,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const hasStartedCourse = !!lastClassViewedId;
    const targetClassId = lastClassViewedId || firstClassId;

    return (
        <GlassCard className="mb-8 overflow-hidden rounded-2xl">
            <div className="flex flex-col md:flex-row md:items-stretch">
                {/* Course Image */}
                <div className="w-full flex-shrink-0 overflow-hidden md:w-[55%]">
                    {course.image_url ? (
                        <img
                            src={course.image_url}
                            alt={course.title}
                            className="h-full w-full object-cover"
                            style={{ minHeight: '200px' }}
                        />
                    ) : (
                        <div className="flex aspect-video w-full items-center justify-center bg-theme-primary md:aspect-auto md:h-full">
                            <BookOpen className="h-12 w-12 text-white" />
                        </div>
                    )}
                </div>

                {/* Course Info */}
                <div className="flex flex-1 flex-col justify-center p-5 md:p-8">
                    {/* Category badge */}
                    <div className="mb-4">
                        <span className="inline-block rounded-full border border-theme-border bg-theme-lightest px-3 py-1 text-sm font-medium text-theme-primary-text">
                            {course.category.name}
                        </span>
                    </div>

                    {/* Title */}
                    <Heading as="h1" className="mb-4 text-2xl leading-tight xl:text-3xl">
                        {course.title}
                    </Heading>

                    {/* Sections count */}
                    <div className="mb-6 flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-400">
                        <PlayCircle size={16} className="text-gray-400 dark:text-neutral-500" />
                        <span>
                            {t('home.sections')}:{' '}
                            <span className="font-semibold text-gray-700 dark:text-neutral-300">
                                {course.sections.length}
                            </span>
                        </span>
                    </div>

                    {/* CTA button */}
                    {targetClassId && (
                        <div>
                            <PrimaryButton
                                as="link"
                                href={route('class-detail', {
                                    slug: course.slug,
                                    class_id: targetClassId,
                                }).toString()}
                            >
                                {hasStartedCourse
                                    ? t('course.continueWhereYouLeftOff')
                                    : t('course.startCourse')}
                            </PrimaryButton>
                        </div>
                    )}
                </div>
            </div>
        </GlassCard>
    );
};

export default CourseHeader;
